import { useMemo, useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useBranding } from "@/context/BrandingContext";

// Count-up hook — animerar från 0 upp till `value` när `active`=true.
// Använder rAF med ease-out så siffran känns "tick-tock" utan att vara stel.
// Re-animera om target ändras stort (>15%) — annars glid till nytt värde
// direkt så slider-drag känns responsivt.
function useCountUp(value, active, duration = 900) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  const startedRef = useRef(false);
  const lastTargetRef = useRef(0);

  useEffect(() => {
    if (!active) return;
    const target = Number(value) || 0;
    const from = startedRef.current ? display : 0;
    const isBigJump = !startedRef.current
      || Math.abs(target - lastTargetRef.current) / Math.max(1, lastTargetRef.current) > 0.15;
    lastTargetRef.current = target;
    startedRef.current = true;

    if (!isBigJump) {
      setDisplay(target); // små slider-nudges: uppdatera direkt
      return;
    }

    const t0 = performance.now();
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      const val = from + (target - from) * easeOut(t);
      setDisplay(Math.round(val));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [value, active, duration]);

  return display;
}

// IntersectionObserver-hook — sätter true en gång när elementet syns.
function useInView(ref, { threshold = 0.35, rootMargin = "0px" } = {}) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect(); // fire once — undvik jitter vid ut-/inscroll
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold, rootMargin]);
  return inView;
}

// Håll denna spegling exakt mot backend/scripts/setup_stripe_products.py
// Volume-tiered: samma pris för alla platser inom ett intervall.
const TIERS = [
  { max: 5, yearly: 7200 },     // 1–5 platser
  { max: 10, yearly: 10800 },   // 6–10 platser
  { max: Infinity, yearly: 14400 }, // 11+ platser (obegränsat)
];
const VAT_RATE = 0.25; // Svensk moms

function fmtSEK(n) {
  return n.toLocaleString("sv-SE", { maximumFractionDigits: 0 });
}

function priceForSeats(seats) {
  for (const t of TIERS) {
    if (seats <= t.max) return t.yearly;
  }
  return TIERS[TIERS.length - 1].yearly;
}

function tierLabel(seats) {
  if (seats <= 5) return "1–5 platser";
  if (seats <= 10) return "6–10 platser";
  return "11+ platser (obegränsat)";
}

const FEATURES = [
  "Tidstämpling med objekt och moment",
  "Automatisk PDF- och CSV-export",
  "Filuppladdning per objekt (PDF, Excel, DXF/AutoCAD)",
  "Zip-arkivering av avslutade objekt",
  "Admin-redigering av tidrapporter med spårbarhet",
  "Bidirektionell API-integration med kaplist-track",
  "Grupperingar och delsummor per moment",
  "Eget företagsnamn, logga och färgtema",
  "Obegränsat antal kollegor kan bjudas in",
];

export default function Pricing() {
  const { companyName } = useBranding();
  const [params, setParams] = useSearchParams();
  const initialSeats = (() => {
    const raw = parseInt(params.get("seats") || "", 10);
    if (Number.isFinite(raw) && raw >= 1 && raw <= 50) return raw;
    return 5;
  })();
  const initialHourlyRate = (() => {
    const raw = parseInt(params.get("rate") || "", 10);
    if (Number.isFinite(raw) && raw >= 300 && raw <= 3000) return raw;
    return 950; // typisk faktureringsnivå för konsult/hantverkare
  })();
  const initialLostHours = (() => {
    const raw = parseFloat(params.get("lost") || "");
    if (Number.isFinite(raw) && raw >= 0.5 && raw <= 10) return raw;
    return 2; // konservativt: 2h/mån/anställd i "borttappad" tid
  })();
  const [seats, setSeats] = useState(initialSeats);
  const [hourlyRate, setHourlyRate] = useState(initialHourlyRate);
  const [lostHours, setLostHours] = useState(initialLostHours);
  const [shareOpen, setShareOpen] = useState(false);
  const shareRef = useRef(null);

  // Håll URL:en synk med räknaren så länken alltid speglar aktuell prislapp.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (seats === 5) next.delete("seats");
    else next.set("seats", String(seats));
    if (hourlyRate === 950) next.delete("rate");
    else next.set("rate", String(hourlyRate));
    if (lostHours === 2) next.delete("lost");
    else next.set("lost", String(lostHours));
    setParams(next, { replace: true });
  }, [seats, hourlyRate, lostHours, params, setParams]);

  // Stäng dela-menyn vid klick utanför.
  useEffect(() => {
    if (!shareOpen) return;
    const onDoc = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) {
        setShareOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [shareOpen]);

  const yearly = useMemo(() => priceForSeats(seats), [seats]);
  const yearlyWithVat = Math.round(yearly * (1 + VAT_RATE));
  const perMonth = Math.round(yearly / 12);
  const perMonthWithVat = Math.round(yearlyWithVat / 12);
  const perSeatPerMonth = seats > 0 ? Math.round(yearly / 12 / seats) : 0;
  const perSeatPerMonthWithVat = seats > 0 ? Math.round(yearlyWithVat / 12 / seats) : 0;

  // ROI-beräkning (använder seats/hourlyRate/lostHours ovan). Publik jämförelse
  // görs mot inkl. moms så kunderna ser sin faktiska månadskostnad.
  const savingsPerMonth = Math.round(seats * hourlyRate * lostHours);
  const savingsPerYear = savingsPerMonth * 12;
  const netPerMonth = savingsPerMonth - perMonthWithVat;
  const roiMultiplier = perMonthWithVat > 0 ? savingsPerMonth / perMonthWithVat : 0;
  const paybackHours = hourlyRate > 0 ? perMonthWithVat / hourlyRate : 0;

  // Tick-tock: animera hero-siffran från 0 upp till besparingen när ROI-
  // sektionen scrollas in i vyn. Ger den psykologiska "wow"-effekten som är
  // svår att skrolla förbi.
  const roiSectionRef = useRef(null);
  const roiInView = useInView(roiSectionRef, { threshold: 0.35 });
  const animatedSavings = useCountUp(savingsPerMonth, roiInView, 1100);
  const animatedYear = useCountUp(savingsPerYear, roiInView, 1400);

  const shareUrl = `${window.location.origin}/pricing?seats=${seats}`;
  const shareSubject = "Kolla in Tidsrapporten — tidrapportering för projektbaserade team";
  const shareBody =
    `Hej!\n\nJag hittade ett tidrapporteringsverktyg som skulle passa oss.\n` +
    `För ${seats} ${seats === 1 ? "medarbetare" : "medarbetare"} kostar det ` +
    `${fmtSEK(yearlyWithVat)} kr/år inkl. moms (${fmtSEK(perMonthWithVat)} kr/mån). 30 dagars gratis prövotid.\n\n` +
    `Se prislistan och räknaren här: ${shareUrl}\n`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Länk kopierad till urklipp");
    } catch {
      toast.error("Kunde inte kopiera — markera och kopiera manuellt");
    }
  };

  const shareNative = async () => {
    if (!navigator.share) {
      copyLink();
      return;
    }
    try {
      await navigator.share({
        title: "Tidsrapporten",
        text: shareBody,
        url: shareUrl,
      });
    } catch {
      // användaren avbröt — ingen åtgärd
    }
  };

  return (
    <div data-testid="pricing-page" className="min-h-screen bg-app">
      {/* Top nav */}
      <div className="border-b border-app">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-xs tracking-[0.2em] uppercase text-text-muted hover:text-brand"
            data-testid="pricing-home-link"
          >
            {companyName}
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="text-sm text-text-muted hover:text-brand"
              data-testid="pricing-login-link"
            >
              Logga in
            </Link>
            <Link
              to="/register"
              className="tk-btn-primary text-sm py-2 px-4"
              data-testid="pricing-signup-nav-button"
            >
              Skapa konto
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-8">
        <div className="mb-2 text-xs tracking-[0.2em] uppercase text-text-muted">
          Prissättning
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-text-primary leading-[1.02] max-w-3xl">
          En plan. <span className="text-brand">Alla funktioner.</span>
          <br />
          Betala bara för aktiva medarbetare.
        </h1>
        <p className="mt-6 text-text-muted text-base max-w-2xl">
          30 dagars gratis prövotid. <strong>Inget kort krävs</strong> för att
          komma igång — testa hela appen med ditt team, aktivera abonnemanget
          först när ni är övertygade. Årlig fakturering. Alla priser inkl. 25 % moms.
        </p>
      </div>

      {/* ROI-callout — värde-argument */}
      <div className="max-w-6xl mx-auto px-6 pb-10">
        <div
          data-testid="pricing-roi-callout"
          className="bg-brand/5 border-l-4 border-brand rounded-r-2xl px-6 md:px-8 py-6 flex flex-col md:flex-row md:items-center gap-4"
        >
          <div className="text-brand font-heading text-5xl leading-none tabular-nums shrink-0">
            1&nbsp;h
          </div>
          <div>
            <div className="font-heading text-xl text-text-primary">
              En bortglömd timdebitering kostar mer än en hel månad här.
            </div>
            <p className="mt-1 text-sm text-text-muted max-w-2xl">
              Med rätt tidrapportering fångar ni upp de fakturerbara timmar som
              annars försvinner i minneslappar och Excel. En enda återfunnen
              timme per månad betalar vanligtvis hela abonnemanget — resten är
              ren vinst.
            </p>
          </div>
        </div>
      </div>

      {/* Scenario / social proof */}
      <div className="max-w-6xl mx-auto px-6 pb-10">
        <div className="flex items-baseline justify-between mb-4">
          <div className="text-xs uppercase tracking-widest text-text-muted">
            Så här kan resultatet se ut
          </div>
          <div className="text-[10px] uppercase tracking-widest text-text-muted italic">
            Exempelscenarier
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              quote:
                "Efter ett halvår såg vi att vi tidigare glömt ta betalt för i snitt 3 timmar per medarbetare och månad. Systemet betalar sig många gånger om.",
              who: "Konsultbolag, 4 medarbetare",
              seats: 4,
              tier: "1–5 platser",
              roi: "4x återbetalning",
            },
            {
              quote:
                "Vi slutade jaga papperslappar. Alla stämplar sina moment direkt i appen, PDF:en till kund tas fram på minuten och admin-tiden halverades.",
              who: "Projektbyrå, 8 medarbetare",
              seats: 8,
              tier: "6–10 platser",
              roi: "≈ 6 h/månad sparad admin",
            },
            {
              quote:
                "Med grupperingen per moment ser vi tydligt vilka projekt som drar över budget — långt innan slutfakturan. Underlagen till kund blir också bättre.",
              who: "Teknikkonsult, 14 medarbetare",
              seats: 14,
              tier: "11+ platser",
              roi: "Färre överraskningar i offerten",
            },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`pricing-scenario-${i}`}
              className="bg-surface border border-app rounded-2xl p-6 flex flex-col"
            >
              <div className="text-brand font-heading text-2xl leading-none">
                ”
              </div>
              <p className="mt-2 text-sm text-text-primary leading-relaxed">
                {s.quote}
              </p>
              <div className="mt-6 pt-4 border-t border-app text-xs">
                <div className="text-text-muted uppercase tracking-widest">
                  {s.who}
                </div>
                <div className="mt-1 flex items-baseline justify-between">
                  <span className="text-text-primary">{s.tier}</span>
                  <span className="text-brand font-medium">{s.roi}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-text-muted italic">
          Exempel baserade på typiska mönster hos projektbaserade konsult- och hantverksverksamheter — ej faktiska kundcitat.
        </p>
      </div>

      {/* Team-size shortcuts */}
      <div className="max-w-6xl mx-auto px-6 pb-10">
        <div className="text-xs uppercase tracking-widest text-text-muted mb-4">
          Vad passar oss?
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              key: "small",
              label: "Litet team",
              range: "1–5 medarbetare",
              example: "Ensamföretagare, familjebolag, mindre konsultbyrå",
              seats: 3,
              tier: "1–5 platser",
              price: "9 000 kr/år",
              perMonth: "750 kr/mån",
            },
            {
              key: "mid",
              label: "Mellanstort",
              range: "6–10 medarbetare",
              example: "Etablerat konsult- eller hantverksbolag",
              seats: 8,
              tier: "6–10 platser",
              price: "13 500 kr/år",
              perMonth: "1 125 kr/mån",
            },
            {
              key: "big",
              label: "Stort företag",
              range: "11+ medarbetare",
              example: "Riksomfattande konsult- eller entreprenadverksamhet",
              seats: 15,
              tier: "11+ platser · obegränsat",
              price: "18 000 kr/år",
              perMonth: "1 500 kr/mån",
            },
          ].map((p) => {
            const selected = seats === p.seats;
            return (
              <button
                key={p.key}
                onClick={() => setSeats(p.seats)}
                data-testid={`pricing-teamsize-${p.key}`}
                className={
                  "text-left rounded-2xl p-6 border transition-all " +
                  (selected
                    ? "bg-brand/10 border-brand"
                    : "bg-surface border-app hover:border-brand/50")
                }
              >
                <div className="flex items-baseline justify-between">
                  <div className="font-heading text-xl text-text-primary">
                    {p.label}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-text-muted">
                    {p.range}
                  </div>
                </div>
                <p className="mt-2 text-sm text-text-muted">{p.example}</p>
                <div className="mt-5 pt-4 border-t border-app flex items-baseline justify-between">
                  <div className="font-heading text-2xl text-brand tabular-nums">
                    {p.price}
                  </div>
                  <div className="text-xs text-text-muted tabular-nums">
                    {p.perMonth}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-text-muted">
          Klicka på ett kort så uppdateras räknaren nedan.
        </p>
      </div>

      {/* Calculator */}
      <div className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: räknare */}
          <div className="bg-surface border border-app rounded-2xl p-8">
            <div className="text-xs uppercase tracking-widest text-text-muted">
              Räknare
            </div>
            <label
              htmlFor="seats"
              className="mt-2 block font-heading text-2xl text-text-primary"
            >
              Hur många medarbetare är ni?
            </label>

            <div className="mt-8 flex items-baseline gap-3">
              <span
                data-testid="pricing-seats-value"
                className="font-heading text-6xl text-brand tabular-nums"
              >
                {seats}
              </span>
              <span className="text-text-muted text-sm">
                {seats === 1 ? "aktiv medarbetare" : "aktiva medarbetare"}
              </span>
            </div>

            <input
              id="seats"
              type="range"
              min="1"
              max="50"
              value={seats}
              onChange={(e) => setSeats(parseInt(e.target.value, 10))}
              data-testid="pricing-seats-slider"
              className="mt-6 w-full accent-[color:var(--brand)]"
            />

            <div className="mt-3 flex items-center justify-between text-xs text-text-muted">
              <span>1</span>
              <span>25</span>
              <span>50+</span>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {[3, 5, 10, 20, 40].map((n) => (
                <button
                  key={n}
                  onClick={() => setSeats(n)}
                  data-testid={`pricing-preset-${n}`}
                  className={
                    "px-4 py-2 rounded-full text-sm border transition-all " +
                    (seats === n
                      ? "border-brand text-brand bg-brand/10"
                      : "border-app text-text-muted hover:text-text-primary")
                  }
                >
                  {n} st
                </button>
              ))}
            </div>

            <p className="mt-8 text-xs text-text-muted leading-relaxed">
              Endast <strong className="text-text-primary">aktiva</strong>{" "}
              admins och anställda räknas. Pausar du en medarbetare debiteras
              hen inte längre — Stripe justerar priset automatiskt vid nästa
              förnyelse.
            </p>
          </div>

          {/* Right: pris */}
          <div className="bg-surface border border-app rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-brand/5 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="text-xs uppercase tracking-widest text-text-muted">
                Din prislapp
              </div>

              <div className="mt-6 flex items-baseline gap-2">
                <span
                  data-testid="pricing-total-year"
                  className="font-heading text-5xl md:text-6xl text-text-primary tabular-nums"
                >
                  {fmtSEK(yearlyWithVat)}
                </span>
                <span className="text-text-muted text-lg">kr / år</span>
              </div>
              <div className="mt-1 text-sm text-text-muted">
                inkl. 25 % moms —{" "}
                <span data-testid="pricing-total-year-net" className="tabular-nums">
                  {fmtSEK(yearly)} kr
                </span>{" "}
                exkl. moms
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-text-muted">
                    Per månad
                  </div>
                  <div
                    data-testid="pricing-total-month"
                    className="mt-1 font-heading text-2xl text-text-primary tabular-nums"
                  >
                    {fmtSEK(perMonthWithVat)} kr
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-text-muted">
                    Per person / månad
                  </div>
                  <div
                    data-testid="pricing-per-seat-month"
                    className="mt-1 font-heading text-2xl text-text-primary tabular-nums"
                  >
                    {fmtSEK(perSeatPerMonthWithVat)} kr
                  </div>
                </div>
              </div>

              <div className="mt-8 border-t border-app pt-6 space-y-2 text-sm">
                <div className="text-xs uppercase tracking-widest text-text-muted mb-3">
                  Ni ligger i intervallet
                </div>
                <div
                  data-testid="pricing-tier-label"
                  className="flex justify-between items-center bg-brand/10 border border-brand/30 rounded-lg px-4 py-3"
                >
                  <span className="text-text-primary font-medium">{tierLabel(seats)}</span>
                  <span className="text-brand font-heading tabular-nums text-lg">
                    {fmtSEK(yearlyWithVat)} kr/år
                  </span>
                </div>
                <div className="mt-3 space-y-1 text-xs text-text-muted">
                  <div className="flex justify-between">
                    <span>1–5 platser</span>
                    <span className="tabular-nums">9 000 kr/år · 750 kr/mån</span>
                  </div>
                  <div className="flex justify-between">
                    <span>6–10 platser</span>
                    <span className="tabular-nums">13 500 kr/år · 1 125 kr/mån</span>
                  </div>
                  <div className="flex justify-between">
                    <span>11+ platser (obegränsat)</span>
                    <span className="tabular-nums">18 000 kr/år · 1 500 kr/mån</span>
                  </div>
                  <div className="pt-2 text-[10px] italic opacity-70">
                    Alla priser inkl. 25 % moms.
                  </div>
                </div>
              </div>

              <Link
                to="/register"
                className="tk-btn-primary block mt-8 text-center py-3"
                data-testid="pricing-cta-button"
              >
                Starta 30 dagar gratis
              </Link>

              {/* Dela med kollega */}
              <div className="relative mt-3" ref={shareRef}>
                <button
                  type="button"
                  onClick={() => setShareOpen((v) => !v)}
                  className="tk-btn-secondary w-full py-3 flex items-center justify-center gap-2"
                  data-testid="pricing-share-button"
                >
                  <span>Dela med kollega</span>
                  <span aria-hidden className="text-xs">↗</span>
                </button>

                {shareOpen && (
                  <div
                    data-testid="pricing-share-menu"
                    className="absolute z-20 left-0 right-0 mt-2 bg-app border border-app rounded-xl p-4 shadow-xl"
                  >
                    <div className="text-xs uppercase tracking-widest text-text-muted">
                      Länk till er prislapp
                    </div>
                    <div className="mt-2 flex gap-2">
                      <input
                        readOnly
                        value={shareUrl}
                        data-testid="pricing-share-url"
                        onClick={(e) => e.target.select()}
                        className="flex-1 bg-surface border border-app rounded-lg px-3 py-2 text-xs text-text-primary font-mono truncate"
                      />
                      <button
                        type="button"
                        onClick={copyLink}
                        data-testid="pricing-share-copy"
                        className="tk-btn-primary text-xs px-3 py-2"
                      >
                        Kopiera
                      </button>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <a
                        href={`mailto:?subject=${encodeURIComponent(
                          shareSubject
                        )}&body=${encodeURIComponent(shareBody)}`}
                        className="tk-btn-secondary text-center text-sm py-2"
                        data-testid="pricing-share-email"
                      >
                        E-post
                      </a>
                      <a
                        href={`sms:?body=${encodeURIComponent(shareBody)}`}
                        className="tk-btn-secondary text-center text-sm py-2"
                        data-testid="pricing-share-sms"
                      >
                        SMS
                      </a>
                    </div>

                    {typeof navigator !== "undefined" && "share" in navigator && (
                      <button
                        type="button"
                        onClick={shareNative}
                        className="tk-btn-secondary w-full mt-2 text-sm py-2"
                        data-testid="pricing-share-native"
                      >
                        Fler alternativ…
                      </button>
                    )}

                    <p className="mt-3 text-[11px] text-text-muted">
                      Länken har {seats} {seats === 1 ? "plats" : "platser"} förvalt.
                      Justera slidern innan du kopierar för att ändra.
                    </p>
                  </div>
                )}
              </div>

              <p className="mt-3 text-center text-xs text-text-muted">
                Ingen debitering under prövoperioden. Säg upp när som helst.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ROI-kalkylator — visa svart-på-vitt vad kunden sparar */}
      <div className="max-w-6xl mx-auto px-6 pb-16" ref={roiSectionRef} data-testid="roi-calculator-section">
        <div className="rounded-3xl overflow-hidden border border-app bg-gradient-to-br from-[color:var(--brand-forest)] to-[#0f1c17] text-white">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            {/* Vänster: kontroller */}
            <div className="lg:col-span-3 p-8 md:p-10">
              <div className="text-xs uppercase tracking-widest text-white/60">
                Vad tjänar ni på Tidsrapporten?
              </div>
              <h2 className="mt-2 font-heading text-3xl md:text-4xl leading-tight">
                Räkna ut er återbetalning
              </h2>
              <p className="mt-3 text-sm text-white/70 max-w-lg">
                De flesta projektteam tappar 1–3 timmar per person och månad i
                icke-fakturerad tid — glömda minuter, felstämplat, oregistrerade
                telefonsamtal. Justera slidrarna nedan så räknar vi ut vad det
                kostar er just nu.
              </p>

              {/* Timpris-slider */}
              <div className="mt-8">
                <div className="flex items-baseline justify-between">
                  <label htmlFor="roi-rate" className="text-sm text-white/80">
                    Genomsnittligt timpris (utfakturerat)
                  </label>
                  <div className="font-heading text-2xl tabular-nums" data-testid="roi-hourly-rate-value">
                    {fmtSEK(hourlyRate)} <span className="text-sm text-white/60">kr/h</span>
                  </div>
                </div>
                <input
                  id="roi-rate"
                  type="range"
                  min="300"
                  max="3000"
                  step="50"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(parseInt(e.target.value, 10))}
                  data-testid="roi-hourly-rate-slider"
                  className="mt-3 w-full accent-[color:var(--brand)]"
                />
                <div className="flex items-center justify-between text-[11px] text-white/40 mt-1">
                  <span>300 kr</span>
                  <span>1&nbsp;500 kr</span>
                  <span>3&nbsp;000 kr</span>
                </div>
              </div>

              {/* Glömda timmar-slider */}
              <div className="mt-6">
                <div className="flex items-baseline justify-between">
                  <label htmlFor="roi-lost" className="text-sm text-white/80">
                    Ofakturerade timmar per person &amp; månad
                  </label>
                  <div className="font-heading text-2xl tabular-nums" data-testid="roi-lost-hours-value">
                    {lostHours.toFixed(1)} <span className="text-sm text-white/60">h</span>
                  </div>
                </div>
                <input
                  id="roi-lost"
                  type="range"
                  min="0.5"
                  max="10"
                  step="0.5"
                  value={lostHours}
                  onChange={(e) => setLostHours(parseFloat(e.target.value))}
                  data-testid="roi-lost-hours-slider"
                  className="mt-3 w-full accent-[color:var(--brand)]"
                />
                <div className="flex items-center justify-between text-[11px] text-white/40 mt-1">
                  <span>0,5 h</span>
                  <span>5 h</span>
                  <span>10 h</span>
                </div>
              </div>

              <p className="mt-6 text-[11px] text-white/50 italic leading-relaxed">
                Antal medarbetare hämtas från räknaren ovan ({seats} st). Ändra slidern där för
                att uppdatera hela ROI-kalkylen. Siffrorna är uppskattningar och
                inga garantier.
              </p>
            </div>

            {/* Höger: resultat */}
            <div className="lg:col-span-2 p-8 md:p-10 bg-black/25 flex flex-col justify-center">
              <div className="text-xs uppercase tracking-widest text-white/60">
                Er beräknade besparing
              </div>

              <div className="mt-3">
                <div
                  className="font-heading text-5xl md:text-6xl leading-none text-[color:var(--brand-rust)] tabular-nums"
                  data-testid="roi-savings-monthly"
                  aria-live="polite"
                >
                  {fmtSEK(animatedSavings)} kr
                </div>
                <div className="mt-1 text-sm text-white/60">per månad</div>
              </div>

              <div
                className="mt-6 rounded-xl bg-white/5 border border-white/10 p-4 text-sm"
                data-testid="roi-comparison"
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-white/70">Tidsrapporten kostar</span>
                  <span className="font-heading text-lg tabular-nums" data-testid="roi-app-cost">
                    {fmtSEK(perMonthWithVat)} kr/mån
                  </span>
                </div>
                <div className="mt-2 flex items-baseline justify-between">
                  <span className="text-white/70">Netto i er ficka</span>
                  <span
                    className={
                      "font-heading text-lg tabular-nums " +
                      (netPerMonth >= 0
                        ? "text-emerald-300"
                        : "text-orange-300")
                    }
                    data-testid="roi-net-savings"
                  >
                    {netPerMonth >= 0 ? "+" : ""}{fmtSEK(netPerMonth)} kr/mån
                  </span>
                </div>
                <div className="mt-3 pt-3 border-t border-white/10 text-center">
                  {roiMultiplier >= 1 ? (
                    <div className="text-sm">
                      <span className="text-white/70">Återbetalar sig </span>
                      <strong
                        className="text-[color:var(--brand-rust)] font-heading text-xl"
                        data-testid="roi-multiplier"
                      >
                        {roiMultiplier.toFixed(1)}×
                      </strong>
                      <span className="text-white/70"> över på månad ett</span>
                    </div>
                  ) : (
                    <div className="text-sm text-white/70">
                      Break-even efter{" "}
                      <strong className="text-white">
                        {paybackHours.toFixed(1)} h
                      </strong>{" "}
                      återfångad tid/månad.
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5 text-xs text-white/50 tabular-nums" data-testid="roi-yearly">
                Årlig besparing: <span className="text-white">{fmtSEK(animatedYear)} kr</span>
              </div>

              <Link
                to="/register"
                data-testid="roi-cta-register"
                className="mt-6 block text-center bg-[color:var(--brand-rust)] hover:bg-[color:var(--brand-rust)]/90 text-white font-medium rounded-xl px-6 py-3.5 transition-colors"
              >
                Prova gratis i 30 dagar →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-6xl mx-auto px-6 pb-24">
        <div className="text-xs uppercase tracking-widest text-text-muted">
          Ingår i alla planer
        </div>
        <h2 className="mt-2 font-heading text-3xl text-text-primary">
          Allt du behöver för att stämpla, dokumentera och fakturera.
        </h2>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className="bg-surface border border-app rounded-xl px-5 py-4 flex items-start gap-3"
              data-testid={`pricing-feature-${i}`}
            >
              <span className="mt-1 text-brand shrink-0">✓</span>
              <span className="text-sm text-text-primary">{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="border-t border-app">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl text-text-primary">
            Vanliga frågor
          </h2>
          <div className="mt-8 space-y-6 text-sm">
            <div>
              <div className="font-heading text-text-primary text-base">
                Krävs kort vid registrering?
              </div>
              <p className="mt-1 text-text-muted">
                Nej. Du skapar konto och kommer igång direkt utan att lämna
                några kortuppgifter. När prövotiden på 30 dagar närmar sig
                slutet får du en påminnelse — då lägger du in kortet i lugn
                och ro om ni vill fortsätta.
              </p>
            </div>
            <div>
              <div className="font-heading text-text-primary text-base">
                Vad händer efter 30 dagar om jag inte aktiverar?
              </div>
              <p className="mt-1 text-text-muted">
                Appen går in i <em>läsläge</em> — ni ser all historik och kan
                exportera gamla rapporter, men kan inte stämpla nytt. All data
                behålls oförändrad. Aktivera abonnemanget när som helst för
                att komma tillbaka till full funktion.
              </p>
            </div>
            <div>
              <div className="font-heading text-text-primary text-base">
                Vad händer när vi anställer fler?
              </div>
              <p className="mt-1 text-text-muted">
                Bjud in nya medarbetare direkt i appen. Priset justeras
                automatiskt vid nästa förnyelse — Stripe delar upp mellanskillnaden
                proportionerligt för den period som återstår.
              </p>
            </div>
            <div>
              <div className="font-heading text-text-primary text-base">
                Kan vi säga upp när som helst?
              </div>
              <p className="mt-1 text-text-muted">
                Ja. Via kundportalen (nås från fakturasidan i appen) säger du upp
                med ett klick. Prenumerationen fortsätter fram till innevarande
                periods slut och därefter stängs kontot.
              </p>
            </div>
            <div>
              <div className="font-heading text-text-primary text-base">
                Hur hanteras moms?
              </div>
              <p className="mt-1 text-text-muted">
                Priser visas inkl. 25 % svensk moms. Stripe redovisar momsen
                separat på fakturan.
              </p>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <Link
              to="/register"
              className="tk-btn-primary"
              data-testid="pricing-faq-cta"
            >
              Skapa konto och testa gratis
            </Link>
            <Link
              to="/login"
              className="tk-btn-secondary"
              data-testid="pricing-faq-login"
            >
              Jag har redan ett konto
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
